import { DeleteOutlined, EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import { Button, Input, Popconfirm, Select, Tooltip, message } from "antd";
import React, { useEffect, useState } from "react";
import {
  Form,
  redirect,
  useActionData,
  useLoaderData,
  useNavigate,
  useRevalidator,
} from "react-router-dom";
import { changeUserPassword, deleteUser, getUserDetails, updateUser } from "../../api/user";
import { getCloudTalkAgents } from "../../api/telephony/cloudtalk";
import { Box } from "../../components/design/box.js";
import { ResponsiveSideDrawer } from "../../components/drawer";
import { FieldWrapper, ProfileUpload } from "../../components/inputs";
import { ValidationError } from "../../library/classes/exceptions/index.js";
import {
  Permission,
  RolePermission,
  UserRoles,
} from "../../library/constants/dynamic.js";
import {
  getFieldErrors,
  getFieldStatus,
} from "../../library/helpers/forms/index.js";
import Styles from "./user-details.module.scss";

const UserDetails = () => {
  const { user } = useLoaderData();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const [userType, setUserType] = useState(user.roles);
  const [permission, setPermission] = useState(user.permisions);
  const [cloudtalkAgentId, setCloudtalkAgentId] = useState(
    user.cloudtalk_agent_id ? String(user.cloudtalk_agent_id) : undefined,
  );
  const [cloudtalkAgents, setCloudtalkAgents] = useState([]);
  const [isArchiving, setIsArchiving] = useState(false);
  const { errors = null } = useActionData() || {};

  useEffect(() => {
    getCloudTalkAgents()
      .then((agents) => {
        if (Array.isArray(agents)) {
          setCloudtalkAgents(agents);
        }
      })
      .catch(() => setCloudtalkAgents([]));
  }, []);

  const handleArchive = async () => {
    try {
      setIsArchiving(true);
      await deleteUser(user.id);
      message.success("User archived successfully!");
      revalidator.revalidate();
      const searchParams = new URLSearchParams(location.search);
      navigate(
        {
          pathname: "..",
          search: `?${searchParams.toString()}`,
        },
        { replace: true, relative: "route" },
      );
    } catch (err) {
      message.error(err.message || "Failed to archive user");
    } finally {
      setIsArchiving(false);
    }
  };

  const handleRolesChange = (roles) => {
    if (roles.length === 0) {
      setUserType([]);
      setPermission([]);
      return;
    }

    let newPermissions = [];
    setUserType(roles);
    roles.forEach((r) => {
      const rolePermissions =
        RolePermission.find(({ id }) => r === id)?.value || [];
      newPermissions = [...newPermissions, ...rolePermissions];
    });
    setPermission([...new Set(newPermissions)]);
  };

  const agentOptions = cloudtalkAgents.map((a) => {
    const isAssignedToOther =
      a.assigned_to && String(a.assigned_to.id) !== String(user.id);
    const assignedLabel = isAssignedToOther
      ? ` [Assigned to: ${a.assigned_to.name || a.assigned_to.email}]`
      : "";
    return {
      value: String(a.id),
      label: `${a.name || "Agent " + a.id} (${a.id}${a.email ? ` • ${a.email}` : ""})${assignedLabel}`,
    };
  });

  // If the user has an assigned agent ID not present in the current agent options, add it as a fallback option
  if (
    cloudtalkAgentId &&
    !agentOptions.some((opt) => opt.value === String(cloudtalkAgentId))
  ) {
    agentOptions.unshift({
      value: String(cloudtalkAgentId),
      label: `Agent ID: ${cloudtalkAgentId}`,
    });
  }

  return (
    <ResponsiveSideDrawer
      open={true}
      title={`User: ${user.name}`}
      extra={
        <Popconfirm
          title="Archive User"
          description={`Are you sure you want to archive "${user.name || user.email}"?`}
          onConfirm={handleArchive}
          okText="Archive"
          cancelText="Cancel"
          okButtonProps={{ danger: true, loading: isArchiving }}
        >
          <Tooltip title="Archive User">
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              loading={isArchiving}
            />
          </Tooltip>
        </Popconfirm>
      }
      onClose={() => {
        const searchParams = new URLSearchParams(location.search);
        navigate(
          {
            pathname: "..",
            search: `?${searchParams.toString()}`,
          },
          { replace: true, relative: "route" },
        );
      }}
    >
      <div className={Styles.drawerContent}>
        <div className={Styles.drawerScrollArea}>
          <ProfileUpload user={user} />

          <Form
            method="post"
            key={`${user.id}-${user.name}`}
            style={{ marginTop: "1rem" }}
            className={Styles.formWrapper}
          >
        <div className={Styles.fieldsRow}>
          <div className={Styles.inputField}>
            <FieldWrapper label="First Name" showRequired>
              <Input
                type="text"
                name="first_name"
                key={user.first_name}
                defaultValue={user.first_name}
                required
                status={getFieldStatus("first_name", errors)}
              />
            </FieldWrapper>
            {getFieldErrors("first_name", errors)}
          </div>
          <div className={Styles.inputField}>
            <FieldWrapper label="Last Name">
              <Input
                type="text"
                name="last_name"
                key={user.last_name}
                defaultValue={user.last_name}
                status={getFieldStatus("last_name", errors)}
              />
            </FieldWrapper>
            {getFieldErrors("last_name", errors)}
          </div>
        </div>

        <div className={Styles.fieldsWrapper}>
          <div className={Styles.inputField}>
            <FieldWrapper label="Email" showRequired>
              <Input
                type="email"
                name="email"
                key={user.email}
                defaultValue={user.email}
                required
                status={getFieldStatus("email", errors)}
              />
            </FieldWrapper>
            {getFieldErrors("email", errors)}
          </div>
        </div>

        <div className={Styles.fieldsWrapper}>
          <div className={Styles.inputField}>
            <FieldWrapper label="Meeting Link">
              <Input
                type="text"
                name="meeting_link"
                key={user.meeting_link}
                defaultValue={user.meeting_link}
              />
            </FieldWrapper>
          </div>
        </div>

        <div className={Styles.fieldsWrapper}>
          <div className={Styles.inputField}>
            <FieldWrapper label="CloudTalk Agent">
              <Select
                placeholder="None / Not Assigned"
                allowClear
                showSearch
                optionFilterProp="label"
                value={cloudtalkAgentId}
                onChange={(val) => setCloudtalkAgentId(val)}
                options={agentOptions}
              />
            </FieldWrapper>
            <input
              type="hidden"
              name="cloudtalk_agent_id"
              value={cloudtalkAgentId || ""}
            />
          </div>
        </div>

        <div className={Styles.fieldsWrapper}>
          <div className={Styles.inputField}>
            <FieldWrapper label="Roles">
              <Select
                key={user.roles}
                options={UserRoles}
                mode="multiple"
                fieldNames={{ value: "id", label: "name" }}
                defaultValue={userType}
                onChange={handleRolesChange}
                status={getFieldStatus("type", errors)}
              />
            </FieldWrapper>
            {getFieldErrors("type", errors)}
            <input type="hidden" name="roles" value={userType.join(",")} />
          </div>
        </div>

        <div className={Styles.fieldsWrapper}>
          <div className={Styles.inputField}>
            <FieldWrapper label="Permission">
              <Select
                key={user.permission}
                options={Permission}
                mode="multiple"
                value={permission}
                fieldNames={{ value: "id", label: "name" }}
                defaultValue={permission}
                onChange={(c) => setPermission(c)}
                status={getFieldStatus("type", errors)}
              />
            </FieldWrapper>
            {getFieldErrors("type", errors)}
            <input type="hidden" name="permission" value={permission.join(",")} />
          </div>
        </div>

        <Button
          type="primary"
          name="action"
          value="updateUser"
          htmlType="submit"
        >
          Update
        </Button>
          </Form>

          <Box containerStyles={{ marginTop: "1rem" }}>
            <Form method="post" className={Styles.formWrapper}>
          <div className={Styles.fieldsWrapper}>
            <div className={Styles.inputField}>
              <FieldWrapper label="New Password" showRequired>
                <Input.Password
                  placeholder="New Password"
                  name="newpassword"
                  iconRender={(visible) =>
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                  status={getFieldStatus("name", errors)}
                  pattern="^(?=.*[0-9])(?=.*[\W_])(?=.*[a-zA-Z])(?=\S+$).{6,}$"
                  title="Password must contain at least one digit, one special character, one letter, and be at least 6 characters."
                  required
                />
              </FieldWrapper>
              {getFieldErrors("name", errors)}
            </div>
          </div>
          <Button
            type="primary"
            name="action"
            value="resetPassword"
            htmlType="submit"
          >
            Reset Password
          </Button>
            </Form>
          </Box>
        </div>
      </div>
    </ResponsiveSideDrawer>
  );
};

UserDetails.Actions = {
  updateUser: async ({ params, data, request }) => {
    const { userId } = params;
    const {
      first_name,
      last_name,
      email,
      roles,
      meeting_link,
      permission,
      cloudtalk_agent_id,
    } = data;
    try {
      await updateUser(userId, {
        first_name,
        last_name,
        email,
        meeting_link,
        cloudtalk_agent_id: cloudtalk_agent_id ? Number(cloudtalk_agent_id) || null : null,
        user_roles:
          permission == ""
            ? roles.split(",").filter(Boolean)
            : roles.split(",").concat(permission.split(",")).filter(Boolean),
      });
      message.success("User Update!");
      try {
        const localUser = JSON.parse(localStorage.getItem("USER") || "{}");
        if (localUser && String(localUser.id) === String(userId)) {
          localUser.cloudtalk_agent_id = cloudtalk_agent_id
            ? Number(cloudtalk_agent_id) || null
            : null;
          localStorage.setItem("USER", JSON.stringify(localUser));
        }
      } catch {
        // ignore
      }
      const url = new URL(request.url);
      return redirect(url.pathname + url.search);
    } catch (error) {
      message.error(error.message);
      if (error instanceof ValidationError) {
        return {
          errors: error.errors,
        };
      }
    }
    return false;
  },
  resetPassword: async ({ params, data }) => {
    const { userId } = params;
    const { newpassword } = data;

    try {
      const msg = await changeUserPassword(userId, newpassword);
      message.success(msg);
      return true;
    } catch (error) {
      if (error instanceof ValidationError) {
        return {
          errors: error.errors,
        };
      }
    }
    return false;
  },
};

UserDetails.Loader = async ({ params }) => {
  const { userId } = params;
  const user = await getUserDetails(userId);
  return { user };
};

export default UserDetails;
